import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SearchPicker from "@/components/SearchPicker";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Loader2, Factory, ShoppingBag, Scissors, Package as PackageIcon,
  AlertTriangle, CheckCircle2, Rocket,
} from "lucide-react";
import { toast } from "sonner";
import { SUPPLY_LABEL, lineMethod } from "@/lib/lineMath";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString("tr-TR");

/**
 * "Reçeteden Üretim" (Order → Production) preview + apply dialog.
 * Calls GET /orders/{id}/production/preview then POST /production/generate.
 */
export default function ProductionPlanDialog({ order, open, onOpenChange, onDone, labels = {}, initialMethod = "" }) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [fifo, setFifo] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingLine, setGeneratingLine] = useState("");
  const [createJobs, setCreateJobs] = useState(true);
  const [createPOs, setCreatePOs] = useState(true);
  const [consumeStock, setConsumeStock] = useState(false);
  // Üretim başlatma + İç/Fason kararı yalnız üretim yetkilisine aittir
  // (backend production.edit / production.manage ile zorunlu kontrol eder).
  const { can } = useAuth();
  const canStartProduction = can?.("production", "edit") || can?.("production", "manage") || false;
  // Üretim Şekli (FAZ 1) — "internal" (İç) | "outsourced" (Fason). Ürün kartı etkilenmez.
  const [prodMethod, setProdMethod] = useState("internal");
  const [fasonSuppliers, setFasonSuppliers] = useState([]);
  const [fasonSupplierId, setFasonSupplierId] = useState("");
  const [fasonQty, setFasonQty] = useState("");
  const [fasonPrice, setFasonPrice] = useState("");
  const [fasonCurrency, setFasonCurrency] = useState("TRY");
  const [fasonDelivery, setFasonDelivery] = useState("");
  const [fasonNote, setFasonNote] = useState("");
  // Tedarik yöntemi Sipariş Detayı'nda seçilir; dialog yalnız seçilen İç/Fason ile başlatır.
  const chosenMethod = order?.supply_method_selected_at && ["internal", "outsourced"].includes(order?.production_method)
    ? order.production_method : null;
  // Hazır Alım: reçete üretimi yok; yalnız Baskı/Nakış gibi application operasyonları başlatılır.
  const readyApps = !!order?.supply_method_selected_at && order?.production_method === "ready_purchase";
  // Satır kararı (Stoktan/İç/Fason/Hazır Alım) varsa her kalem kendi yöntemiyle başlar
  const hasDec = (order?.line_items || []).some((l) => l.stock_qty != null);
  const decMethods = [...new Set((order?.line_items || [])
    .filter((l) => l.stock_qty != null && !l.production_started)
    .map((l) => lineMethod(order, l)).filter((m) => m === "internal" || m === "outsourced"))];
  const allowed = hasDec ? decMethods : (chosenMethod ? [chosenMethod] : []);
  const canStart = canStartProduction && (hasDec || !!chosenMethod || readyApps);

  useEffect(() => {
    if (!open || !order) { setPlan(null); setFifo(null); return; }
    // Üretim şekli state'i sıfırla (varsayılan: seçilen / ilk uygun yöntem)
    setProdMethod(allowed.includes(initialMethod) ? initialMethod : (allowed[0] || "internal"));
    setFasonSupplierId(""); setFasonQty(""); setFasonPrice("");
    setFasonCurrency("TRY"); setFasonDelivery(""); setFasonNote("");
    (async () => {
      setLoading(true);
      try {
        const [p, f] = await Promise.all([
          api.get(`/orders/${order.id}/production/preview`),
          api.get(`/orders/${order.id}/production/fifo-preview`).catch(() => ({ data: null })),
          api.get("/suppliers").then((r) => setFasonSuppliers(r.data || [])).catch(() => {}),
        ]);
        setPlan(p.data);
        setFifo(f.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, order?.id]);

  const generate = async (lineIds = []) => {
    if (!hasDec && !chosenMethod && !readyApps) {
      toast.error("Önce Sipariş Detayı'nda tedarik yöntemi seçin");
      return;
    }
    if (prodMethod === "outsourced" && !fasonSupplierId) {
      toast.error("Fason üretim için fason üretici seçin");
      return;
    }
    setGenerating(true);
    setGeneratingLine(lineIds[0] || "");
    try {
      const { data } = await api.post(`/orders/${order.id}/production/generate`, {
        create_department_jobs: createJobs,
        create_purchase_orders: createPOs,
        consume_stock: consumeStock,
        line_ids: lineIds,
        production_method: prodMethod,
        ...(prodMethod === "outsourced" ? {
          outsourced_supplier_id: fasonSupplierId,
          outsourced_quantity: fasonQty === "" ? null : Number(fasonQty),
          outsourced_unit_price: fasonPrice === "" ? null : Number(fasonPrice),
          outsourced_currency: fasonCurrency,
          outsourced_delivery_date: fasonDelivery || null,
          outsourced_note: fasonNote || null,
        } : {}),
      });
      toast.success(data.message || "Üretim planı oluşturuldu");
      if (lineIds.length) {
        // Kalem modu: dialog açık kalır, preview tazelenir
        const p = await api.get(`/orders/${order.id}/production/preview`);
        setPlan(p.data);
      } else {
        onOpenChange(false);
      }
      onDone?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); setGeneratingLine(""); }
  };

  // Kalem modu: çok kalemli sipariş + order-level üretim henüz başlamamış.
  // Satır bazlı başlatmalar diğer satırları BLOKE ETMEZ (order_level_started).
  // any_started: herhangi bir kanıt (order-level job, line-level job, satır bayrağı,
  // fiziksel aşama) → TÜM-sipariş yeniden başlatma ve yeni job oluşturma kilitlenir.
  const orderLevelStarted = !!plan && (plan.order_level_started ?? plan.production_started);
  const anyStarted = !!plan && (plan.any_started ?? plan.production_started);
  const lineMode = !!plan && !orderLevelStarted && (plan.lines_status || []).length > 1
                   && (plan.lines_status || []).some((ls) => !ls.production_started);
  const recipeMissing = !!plan && (plan.lines_status || []).some((ls) => ls.recipe_missing && ls.needs_production
    && ls.supply_type !== "traded" && !["stock", "ready_purchase", "outsourced"].includes(ls.supply_method));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="production-plan-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <Factory className="w-5 h-5 text-blue-500" />
            {anyStarted ? "Üretim Reçetesi / Malzeme İhtiyacı" : "Reçeteden Üretim"} — {order?.order_no}
          </DialogTitle>
          <div className="text-xs text-slate-400 mt-1">
            {order?.customer_name} · {order?.product_name} · {fmtInt(plan?.current_production_qty ?? order?.quantity)} adet
            {anyStarted && !lineMode && (
              <span data-testid="production-readonly-badge" className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-300 bg-emerald-950/50 border border-emerald-900 rounded-sm px-1.5 py-0.5">
                Üretim başladı — salt okunur
              </span>
            )}
          </div>
        </DialogHeader>

        {loading || !plan ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {recipeMissing && (
              <Card className="bg-amber-950/20 border-amber-900/50 rounded-sm p-4" data-testid="no-bom-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-amber-200 mb-1">Reçete Eksik</div>
                    <div className="text-xs text-slate-300">
                      Üretime reçetesiz başlanacak. Kumaş ve aksesuar sarf işlemleri için reçete daha sonra tamamlanmalıdır.
                      <span className="text-slate-500"> (Ürünler → "Reçete")</span>
                    </div>
                  </div>
                </div>
              </Card>
            )}
            <div className="grid grid-cols-3 gap-3">
              {(() => {
                // Hazır Alım: ürün+varyant+depo stoğuna göre tedarik gereken adet (reçeteden bağımsız)
                const tradedNeed = (plan.traded_rows || []).reduce((t, r) => t + Number(r.shortage || 0), 0);
                const tone = !plan.all_stock_ok ? "red" : tradedNeed > 0 ? "amber" : "emerald";
                const cls = { red: "bg-red-950/20 border-red-900/50", amber: "bg-amber-950/20 border-amber-900/50", emerald: "bg-emerald-950/20 border-emerald-900/50" }[tone];
                return (
                  <Card className={`border rounded-sm p-3 ${cls}`} data-testid="plan-stock-status">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">Stok Durumu</div>
                    <div className={`text-sm font-semibold ${{ red: "text-red-300", amber: "text-amber-300", emerald: "text-emerald-300" }[tone]} flex items-center gap-1.5`}>
                      {!plan.all_stock_ok ? <><AlertTriangle className="w-4 h-4" /> Eksik Var</>
                        : tradedNeed > 0 ? <><AlertTriangle className="w-4 h-4" /> Tedarik Gerekli</>
                        : <><CheckCircle2 className="w-4 h-4" /> Yeterli</>}
                    </div>
                    {tradedNeed > 0 && (
                      <div className="text-[11px] text-amber-300 font-mono mt-0.5" data-testid="plan-traded-need">
                        Hazır Alım: {fmt(tradedNeed)} adet tedarik gerekli
                      </div>
                    )}
                  </Card>
                );
              })()}
              <Card className="bg-blue-950/20 border-blue-900/50 border rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">Eksik Malzeme Tutarı</div>
                <div className="text-sm font-mono font-semibold text-blue-300">
                  ₺{fmt(plan.estimated_shortage_cost)}
                </div>
              </Card>
              <Card className="bg-purple-950/20 border-purple-900/50 border rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">Departman İşleri</div>
                <div className="text-sm font-semibold text-purple-300">
                  {plan.department_rows.length} iş emri
                </div>
              </Card>
            </div>

            {/* Sipariş Kalemleri — kalem bazlı üretime başlama */}
            {lineMode && (
              <Card className="bg-slate-950/40 border-slate-800 rounded-sm overflow-hidden" data-testid="line-start-card">
                <div className="px-4 py-2 border-b border-slate-800 bg-slate-900/50 text-xs font-semibold text-slate-100">
                  Sipariş Kalemleri — Kalem Bazlı Üretime Başla
                </div>
                <div className="divide-y divide-slate-800/60">
                  {plan.lines_status.map((ls) => {
                    const lm = ls.supply_method;
                    const otherMethod = hasDec && ["internal", "outsourced"].includes(lm) && lm !== prodMethod;
                    return (
                    <div key={ls.line_id} className="flex items-center gap-3 px-4 py-2" data-testid={`line-row-${ls.line_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-200 truncate">
                          {ls.product_name}
                          {ls.recipe_missing && ls.needs_production && ls.supply_type !== "traded" && !["stock", "ready_purchase", "outsourced"].includes(lm) && (
                            <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-amber-300 bg-amber-950/50 border border-amber-900 px-1.5 py-0.5 rounded-sm" data-testid={`line-recipe-missing-${ls.line_id}`}>
                              Reçete Eksik
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500">
                          {fmtInt(ls.quantity)} adet · {labels[ls.production_stage] || ls.production_stage || "—"}
                          {hasDec && (
                            <span className="ml-1 text-slate-300" data-testid={`line-method-${ls.line_id}`}>
                              · {Number(ls.stock_qty || 0) > 0 ? `Stoktan ${fmtInt(ls.stock_qty)}` : ""}
                              {Number(ls.stock_qty || 0) > 0 && lm !== "stock" ? " + " : ""}
                              {lm !== "stock" ? `${SUPPLY_LABEL[lm] || lm} ${fmtInt(ls.remaining_qty)}` : ""}
                            </span>
                          )}
                          {ls.needs_production && (ls.supply_type === "traded" || readyApps || ["stock", "ready_purchase"].includes(lm)) && (
                            <span className="ml-1 text-cyan-400" data-testid={`line-app-only-${ls.line_id}`}>· yalnız operasyon (Baskı/Nakış)</span>
                          )}
                        </div>
                      </div>
                      {lm !== "outsourced" && (
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm border ${
                        ls.material_ok
                          ? "text-emerald-300 bg-emerald-950/50 border-emerald-900"
                          : "text-amber-300 bg-amber-950/50 border-amber-900"
                      }`} data-testid={`line-material-${ls.line_id}`}>
                        {ls.material_ok ? "Malzeme Hazır" : "Malzeme Bekliyor"}
                      </span>
                      )}
                      {!ls.needs_production ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-slate-400 bg-slate-900 border border-slate-700 px-1.5 py-0.5 rounded-sm" data-testid={`line-no-production-${ls.line_id}`}>
                          {hasDec && SUPPLY_LABEL[lm] ? SUPPLY_LABEL[lm] : "Ticari"} · Üretim Gerekmez
                        </span>
                      ) : ls.production_started ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-blue-300 bg-blue-950/50 border border-blue-900 px-1.5 py-0.5 rounded-sm" data-testid={`line-started-${ls.line_id}`}>
                          {lm === "outsourced" ? "Fasonda" : "Üretimde"}
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          data-testid={`line-start-${ls.line_id}`}
                          disabled={generating || !canStart || otherMethod}
                          title={!canStartProduction ? "Bu aşamada işlem yapma yetkiniz yok"
                            : otherMethod ? `Bu kalem ${SUPPLY_LABEL[lm]} — üstten ${SUPPLY_LABEL[lm]} seçin` : undefined}
                          onClick={() => generate([ls.line_id])}
                          className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-[10px] font-semibold h-7 px-2"
                        >
                          {generating && generatingLine === ls.line_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><Rocket className="w-3 h-3 mr-1" /> {lm === "outsourced" ? "Fasona Başlat" : "Üretime Başla"}</>}
                        </Button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Fabric shortage */}
            <Card className="bg-slate-950/40 border-slate-800 rounded-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-800 bg-slate-900/50 flex items-center gap-2">
                <Scissors className="w-3.5 h-3.5 text-blue-400" />
                <div className="text-xs font-semibold text-slate-100">Kumaş İhtiyacı</div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-800">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kumaş</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Gerekli</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Stok</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Eksik</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Alım ₺</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.fabric_rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-500 text-xs">Kumaş satırı yok</TableCell></TableRow>
                  ) : plan.fabric_rows.map((r) => (
                    <TableRow key={r.fabric_id} className="border-slate-800" data-testid={`fabric-need-${r.fabric_id}`}>
                      <TableCell className="text-xs">
                        <div className="font-medium text-slate-100">{r.name}</div>
                        <div className="text-slate-500 font-mono text-[10px]">{r.code}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-200">{fmt(r.required)} {r.unit}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-400">{fmt(r.available)} {r.unit}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {r.ok ? <span className="text-emerald-400">✓</span> : <span className="text-red-400 font-semibold">{fmt(r.shortage)} {r.unit}</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">{r.primary_supplier_name || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-blue-400">
                        {r.shortage > 0 ? `₺${fmt(r.shortage_cost)}` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Accessory shortage */}
            <Card className="bg-slate-950/40 border-slate-800 rounded-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-800 bg-slate-900/50 flex items-center gap-2">
                <PackageIcon className="w-3.5 h-3.5 text-purple-400" />
                <div className="text-xs font-semibold text-slate-100">Aksesuar İhtiyacı</div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-800">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Aksesuar</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Gerekli</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Stok</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Eksik</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Alım ₺</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.accessory_rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-500 text-xs">Aksesuar satırı yok</TableCell></TableRow>
                  ) : plan.accessory_rows.map((r) => (
                    <TableRow key={r.accessory_id} className="border-slate-800" data-testid={`accessory-need-${r.accessory_id}`}>
                      <TableCell className="text-xs">
                        <div className="font-medium text-slate-100">{r.name}</div>
                        <div className="text-slate-500 font-mono text-[10px]">{r.code}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-200">{fmt(r.required)} {r.unit}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-400">{fmt(r.available)} {r.unit}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {r.ok ? <span className="text-emerald-400">✓</span> : <span className="text-red-400 font-semibold">{fmt(r.shortage)} {r.unit}</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">{r.primary_supplier_name || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-blue-400">
                        {r.shortage > 0 ? `₺${fmt(r.shortage_cost)}` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Department jobs */}
            <Card className="bg-slate-950/40 border-slate-800 rounded-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-800 bg-slate-900/50 flex items-center gap-2">
                <Factory className="w-3.5 h-3.5 text-emerald-400" />
                <div className="text-xs font-semibold text-slate-100">
                  {plan.production_started ? "Mevcut İş Emirleri" : "Oluşturulacak İş Emirleri"}
                </div>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {plan.department_rows.length === 0 ? (
                  <div className="text-xs text-slate-500">Reçetede operasyon yok</div>
                ) : plan.department_rows.map((d) => (
                  <Badge key={`${d.department}${d.application ? "-app" : ""}`}
                    className={`rounded-sm border text-[11px] uppercase tracking-wider font-mono ${
                      d.already_has_job
                        ? "bg-slate-800 text-slate-400 border-slate-700"
                        : "bg-emerald-950/40 text-emerald-300 border-emerald-900"
                    }`}
                    data-testid={`dept-job-${d.department}${d.application ? "-app" : ""}`}>
                    {d.application ? `${d.label} · ${fmt(d.quantity)} adet` : `${d.label} · ${fmt(d.total_minutes)} dk (${fmt(d.total_hours)} sa)`}
                    {d.already_has_job && <span className="ml-1 opacity-70">· mevcut</span>}
                  </Badge>
                ))}
              </div>
            </Card>

            {/* Options — üretim başladıysa gizli (ikinci kez oluşturma YOK);
                satır modunda başlamamış satırlar için görünür kalır */}
            {(!anyStarted || lineMode) && (
            <div className="space-y-2 border-t border-slate-800 pt-3">
              {/* Üretim Şekli — FAZ 1: emir bazında İç/Fason (ürün kartı değişmez) */}
              <div className="space-y-2 pb-1" data-testid="production-method-block">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Üretim Şekli</div>
                <div className="flex gap-1.5">
                  {[["internal", "İç Üretim"], ["outsourced", "Fason Üretim"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`production-method-${v}`}
                      disabled={!canStartProduction || !allowed.includes(v)}
                      title={canStartProduction ? (!allowed.includes(v) ? (hasDec ? "Bu yöntemde başlatılacak kalem yok" : "Tedarik yöntemi Sipariş Detayı'nda seçilir") : undefined) : "Bu aşamada işlem yapma yetkiniz yok"}
                      onClick={() => setProdMethod(v)}
                      className={`px-3 py-1.5 text-[11px] rounded-sm border transition-colors font-semibold uppercase tracking-wider ${
                        !canStartProduction || !allowed.includes(v) ? "border-slate-800 text-slate-600 cursor-not-allowed"
                          : prodMethod === v
                          ? (v === "outsourced" ? "border-amber-500 bg-amber-950/40 text-amber-300"
                                                : "border-blue-500 bg-blue-950/40 text-blue-300")
                          : "border-slate-700 bg-transparent text-slate-400 hover:border-slate-600"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {canStartProduction && !chosenMethod && !hasDec && (
                  <div className="text-[10px] text-amber-300/90 border border-amber-900/50 bg-amber-950/20 rounded-sm px-2.5 py-1.5" data-testid="production-method-missing">
                    {readyApps
                      ? "Hazır Alım seçili — reçete üretimi yapılmaz; yalnız Baskı/Nakış gibi üretime gönderilen operasyonlar başlatılır."
                      : "Tedarik yöntemi seçilmedi. Üretime başlamak için Sipariş Detayı'nda İç Üretim veya Fason Üretim seçin."}
                  </div>
                )}
                {canStartProduction && hasDec && (
                  <div className="text-[10px] text-slate-300 border border-slate-800 bg-slate-950/40 rounded-sm px-2.5 py-1.5" data-testid="production-method-per-line">
                    Kalemler kendi tedarik yöntemiyle başlar: yalnız seçili yöntemdeki ({SUPPLY_LABEL[prodMethod]}) kalemler ve Baskı/Nakış operasyonları başlatılır.
                    {prodMethod === "outsourced" && " Fason kalem için iç üretim iş emri (Kesim/Dikim) ve reçete malzeme taslağı oluşturulmaz."}
                  </div>
                )}
                {prodMethod === "outsourced" && !hasDec && (
                  <div className="text-[10px] text-amber-200/80" data-testid="production-method-fason-note">
                    Fason üretimde iç üretim iş emri (Kesim/Dikim) ve reçete malzeme taslağı oluşturulmaz.
                  </div>
                )}
                {!canStartProduction && (
                  <div className="text-[10px] text-rose-300/90 border border-rose-900/50 bg-rose-950/20 rounded-sm px-2.5 py-1.5" data-testid="production-start-noperm">
                    Üretim başlatma ve İç/Fason üretim kararı için üretim yetkisi (production.edit/manage) gerekir. Reçeteyi görüntüleyebilirsiniz ancak işlem yapamazsınız.
                  </div>
                )}
                {prodMethod === "outsourced" && (
                  <div className="rounded-sm border border-amber-900/50 bg-amber-950/10 p-3 space-y-2" data-testid="fason-fields">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Fason Üretici *</Label>
                      <SearchPicker
                        items={fasonSuppliers}
                        value={fasonSupplierId}
                        onChange={setFasonSupplierId}
                        getId={(s) => s.id}
                        getLabel={(s) => s.name || ""}
                        getSubtitle={(s) => [s.tax_no, s.phone].filter(Boolean).join(" · ")}
                        filter={(s, q) => {
                          const f = (x) => (x || "").toLocaleLowerCase("tr-TR");
                          const t = f(q);
                          return f(s.name).includes(t) || f(s.tax_no).includes(t) || f(s.phone).includes(t);
                        }}
                        placeholder="Firma ünvanı / vergi no / telefon yazın…"
                        minChars={1}
                        testId="fason-supplier-picker"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Fason Miktarı</Label>
                        <Input data-testid="fason-quantity" type="number" min="0" step="1" value={fasonQty}
                          onChange={(e) => setFasonQty(e.target.value)}
                          className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-8 text-xs font-mono" placeholder="adet" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Birim Fason Fiyatı</Label>
                        <div className="flex gap-1.5 mt-1">
                          <Input data-testid="fason-unit-price" type="number" min="0" step="0.01" value={fasonPrice}
                            onChange={(e) => setFasonPrice(e.target.value)}
                            className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs font-mono flex-1" placeholder="0,00" />
                          <select data-testid="fason-currency" value={fasonCurrency}
                            onChange={(e) => setFasonCurrency(e.target.value)}
                            className="bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-1.5">
                            {["TRY", "USD", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Tahmini Teslim Tarihi</Label>
                        <Input data-testid="fason-delivery-date" type="date" value={fasonDelivery}
                          onChange={(e) => setFasonDelivery(e.target.value)}
                          className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Not</Label>
                        <Input data-testid="fason-note" value={fasonNote} onChange={(e) => setFasonNote(e.target.value)}
                          className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-8 text-xs" placeholder="Opsiyonel" />
                      </div>
                    </div>
                    <div className="text-[10px] text-amber-200/70">
                      Bu seçim yalnızca bu üretim emrine aittir — ürün kartı "Üretilen" olarak kalır.
                    </div>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
                <Checkbox checked={createJobs} onCheckedChange={setCreateJobs}
                  data-testid="option-create-jobs"
                  className="border-slate-600 data-[state=checked]:bg-blue-600" />
                <ShoppingBag className="w-4 h-4 text-emerald-400" />
                Departman iş emirlerini oluştur
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
                <Checkbox checked={createPOs} onCheckedChange={setCreatePOs}
                  data-testid="option-create-pos"
                  className="border-slate-600 data-[state=checked]:bg-blue-600" />
                <ShoppingBag className="w-4 h-4 text-blue-400" />
                Eksik malzemeler için satın alma taslağı oluştur
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-200">
                <Checkbox checked={consumeStock} onCheckedChange={setConsumeStock}
                  data-testid="option-consume-stock"
                  className="border-slate-600 data-[state=checked]:bg-blue-600 mt-0.5" />
                <PackageIcon className="w-4 h-4 text-purple-400 mt-0.5" />
                <span className="flex-1">
                  Malzemeleri Stoktan Otomatik Düş (<span className="font-mono text-purple-300">FIFO</span>)
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    En eski lot'tan başlayarak reçetedeki miktarlar kadar tüketilir. Stok hareketi kaydı oluşur.
                  </div>
                </span>
              </label>
            </div>
            )}

            {/* FIFO allocation preview — üretim başladıysa gizli (satır modunda açık kalır) */}
            {(!anyStarted || lineMode) && consumeStock && fifo && (fifo.fabric_rows?.length > 0 || fifo.accessory_rows?.length > 0) && (
              <Card className="bg-purple-950/20 border-purple-900/40 rounded-sm overflow-hidden"
                data-testid="fifo-preview-card">
                <div className="px-4 py-2 border-b border-purple-900/40 bg-purple-950/30 flex items-center gap-2">
                  <PackageIcon className="w-3.5 h-3.5 text-purple-300" />
                  <div className="text-xs font-semibold text-purple-100">FIFO Lot Tüketim Planı</div>
                  {!fifo.all_allocated && (
                    <Badge className="ml-auto bg-amber-900/40 text-amber-200 border-amber-900 text-[10px] rounded-sm">
                      Bazı malzemelerde eksik lot var
                    </Badge>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-purple-950/40">
                      <TableRow className="border-purple-900/40 hover:bg-transparent">
                        <TableHead className="text-[10px] uppercase text-purple-200">Malzeme</TableHead>
                        <TableHead className="text-[10px] uppercase text-purple-200 text-right">Gerekli</TableHead>
                        <TableHead className="text-[10px] uppercase text-purple-200">Lot / Depo</TableHead>
                        <TableHead className="text-[10px] uppercase text-purple-200 text-right">Kullanılacak</TableHead>
                        <TableHead className="text-[10px] uppercase text-purple-200 text-right">Kalan</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...(fifo.fabric_rows || []), ...(fifo.accessory_rows || [])].map((mat, mIdx) => (
                        mat.allocations.length === 0 ? (
                          <TableRow key={`${mat.material_id}-empty`}
                            className="border-purple-900/40 bg-rose-950/20"
                            data-testid={`fifo-row-empty-${mIdx}`}>
                            <TableCell className="text-xs text-slate-200">{mat.material_code} · {mat.material_name}</TableCell>
                            <TableCell className="text-xs text-right text-slate-300 font-mono">
                              {fmt(mat.required)} {mat.unit}
                            </TableCell>
                            <TableCell colSpan={3} className="text-xs text-rose-300">
                              Aktif lot yok — {fmt(mat.shortfall)} {mat.unit} eksik
                            </TableCell>
                          </TableRow>
                        ) : mat.allocations.map((alloc, aIdx) => (
                          <TableRow key={`${mat.material_id}-${alloc.lot_id}`}
                            className="border-purple-900/40"
                            data-testid={`fifo-row-${mIdx}-${aIdx}`}>
                            <TableCell className="text-xs text-slate-200">
                              {aIdx === 0 && <>{mat.material_code} · {mat.material_name}</>}
                            </TableCell>
                            <TableCell className="text-xs text-right text-slate-300 font-mono">
                              {aIdx === 0 && <>{fmt(mat.required)} {mat.unit}</>}
                            </TableCell>
                            <TableCell className="text-xs text-slate-200 font-mono">
                              {alloc.lot_no}
                              {alloc.warehouse_location && (
                                <span className="text-slate-500 ml-1">· {alloc.warehouse_location}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-right text-emerald-300 font-mono font-semibold">
                              {fmt(alloc.qty_to_take)} {mat.unit}
                            </TableCell>
                            <TableCell className="text-xs text-right text-slate-400 font-mono">
                              {fmt(alloc.qty_remaining_after)} {mat.unit}
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {!fifo.all_allocated && (
                  <div className="px-4 py-2 border-t border-purple-900/40 bg-amber-950/20 text-[11px] text-amber-200">
                    Eksik olan miktarlar tüketilemez — üretim başlatılırsa mevcut lotlardaki miktar kadar düşer.
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="plan-close-btn"
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
            {anyStarted ? "Kapat" : "İptal"}
          </Button>
          {!anyStarted && !lineMode && (
          <Button onClick={() => generate()} disabled={generating || loading || !plan || !canStart}
            data-testid="generate-production-btn"
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-2" /> Üretime Başla</>}
          </Button>
          )}
          {anyStarted && !lineMode && (
          <span data-testid="production-started-note"
            className="text-[10px] uppercase tracking-widest font-semibold text-emerald-400/90 self-center">
            Üretim başladı — yeniden başlatılamaz
          </span>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
